import PropTypes from 'prop-types';

/**
 * Base field definition props
 */
export const BaseFieldDef = PropTypes.shape({
  id: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  label: PropTypes.string,
  helpText: PropTypes.string,
  required: PropTypes.bool,
  visibleWhen: PropTypes.object, // Simple equals match for now
  validate: PropTypes.shape({
    pattern: PropTypes.string,
    min: PropTypes.number,
    max: PropTypes.number,
    maxLength: PropTypes.number
  })
});

/**
 * Single select field definition
 */
export const SingleSelectDef = PropTypes.shape({
  ...BaseFieldDef,
  type: PropTypes.oneOf(['single_select']).isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      description: PropTypes.string
    })
  ).isRequired
});

/**
 * Multi select field definition
 */
export const MultiSelectDef = PropTypes.shape({
  ...BaseFieldDef,
  type: PropTypes.oneOf(['multi_select']).isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  maxSelected: PropTypes.number
});

/**
 * Range field definition
 */
export const RangeDef = PropTypes.shape({
  ...BaseFieldDef,
  type: PropTypes.oneOf(['range']).isRequired,
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  step: PropTypes.number,
  format: PropTypes.shape({
    prefix: PropTypes.string,
    suffix: PropTypes.string
  })
});

/**
 * Text input field definition (unified single-line and multiline)
 */
export const TextInputDef = PropTypes.shape({
  ...BaseFieldDef,
  type: PropTypes.oneOf(['text', 'input']).isRequired,
  multiline: PropTypes.bool, // true for textarea, false/undefined for single-line input
  placeholder: PropTypes.string
});

/**
 * Number field definition
 */
export const NumberDef = PropTypes.shape({
  ...BaseFieldDef,
  type: PropTypes.oneOf(['number']).isRequired,
  step: PropTypes.number
});

/**
 * Checkbox field definition
 */
export const CheckboxDef = PropTypes.shape({
  ...BaseFieldDef,
  type: PropTypes.oneOf(['checkbox']).isRequired
});

/**
 * All field definitions (union type)
 */
export const FieldDef = PropTypes.oneOfType([
  SingleSelectDef,
  MultiSelectDef,
  RangeDef,
  TextInputDef, // Unified text/input field
  NumberDef,
  CheckboxDef
]);

/**
 * Next rule definitions
 */
export const NextRule = PropTypes.oneOfType([
  // Simple goto
  PropTypes.shape({
    goto: PropTypes.string.isRequired
  }),
  // Branching logic
  PropTypes.shape({
    branch: PropTypes.arrayOf(
      PropTypes.shape({
        when: PropTypes.object,
        goto: PropTypes.string.isRequired,
        default: PropTypes.bool
      })
    ).isRequired
  }),
  // Server resolution
  PropTypes.shape({
    server: PropTypes.shape({
      url: PropTypes.string.isRequired,
      method: PropTypes.oneOf(['POST', 'GET'])
    }).isRequired
  })
]);

/**
 * Form step page definition
 */
export const FormStepPage = PropTypes.shape({
  id: PropTypes.string.isRequired,
  kind: PropTypes.oneOf(['form_step']).isRequired,
  title: PropTypes.string,
  layout: PropTypes.shape({
    columns: PropTypes.oneOf([1, 2, 3]),
    gap: PropTypes.oneOf(['sm', 'md', 'lg'])
  }),
  fields: PropTypes.arrayOf(FieldDef).isRequired,
  next: NextRule.isRequired
});

/**
 * Result page definition
 */
export const ResultPage = PropTypes.shape({
  id: PropTypes.string.isRequired,
  kind: PropTypes.oneOf(['result']).isRequired,
  render: PropTypes.shape({
    strategy: PropTypes.oneOf(['replace_component', 'overlay_render', 'article_render']).isRequired,
    instructions: PropTypes.array
  }).isRequired
});

/**
 * Flow data definition
 */
export const FlowData = PropTypes.shape({
  startPageId: PropTypes.string.isRequired,
  pages: PropTypes.arrayOf(PropTypes.oneOfType([FormStepPage, ResultPage])).isRequired
});

/**
 * Flow context definition
 */
export const FlowContext = PropTypes.shape({
  flowId: PropTypes.string.isRequired,
  answers: PropTypes.object.isRequired,
  branch: PropTypes.string
});

/**
 * Resolve request for server transitions
 */
export const ResolveRequest = PropTypes.shape({
  flowId: PropTypes.string.isRequired,
  pageId: PropTypes.string.isRequired,
  answers: PropTypes.object.isRequired
});

/**
 * Resolve response from server
 */
export const ResolveResponse = PropTypes.shape({
  goto: PropTypes.string,
  branch: PropTypes.string,
  renderInstructions: PropTypes.array
});

/**
 * Main ChoiceFlow props
 */
export const ChoiceFlowProps = PropTypes.shape({
  id: PropTypes.string.isRequired,
  title: PropTypes.string,
  progress: PropTypes.bool,
  progressMode: PropTypes.oneOf(['pages', 'required_fields']),
  persistKey: PropTypes.string,
  data: FlowData.isRequired,
  onComponentClick: PropTypes.func
});

/**
 * Field renderer props
 */
export const FieldRendererProps = PropTypes.shape({
  def: FieldDef.isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  error: PropTypes.string
});
